import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import { UseCertificatePreviewReturn, PDFGenerationOptions } from '../../../types/certificate';
import { generateReportId, generateQRCodeContent } from '../../../utils/validation';

/**
 * 合格证预览自定义Hook
 * 管理PDF生成、下载和二维码生成逻辑
 */

interface UseCertificatePreviewProps {
  certificateId?: string;
  qrCodeContent?: string;
}

export const useCertificatePreview = ({ 
  certificateId,
  qrCodeContent
}: UseCertificatePreviewProps = {}): UseCertificatePreviewReturn => {
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const certificateRef = useRef<HTMLDivElement>(null);
  
  // 生成唯一的报告ID，只在Hook初始化时生成一次
  // 这个reportId将用于后续的文件关联
  const reportId = useMemo(() => generateReportId(), []);
  
  // 生成适合微信扫码的URL，指向下载页面
  const reportUrl = useMemo(() => generateQRCodeContent(reportId), [reportId]);
  
  // 使用传入的qrCodeContent或默认使用微信友好的URL
  const finalQrCodeContent = qrCodeContent || reportUrl;

  // 生成二维码
  const generateQRCode = useCallback(async (content: string): Promise<string> => {
    try {
      const qrDataUrl = await QRCode.toDataURL(content, {
        width: 120, // 回退到原来的尺寸
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M', // 回退到中等级别
        type: 'image/png'
      });
      setQrCodeDataUrl(qrDataUrl);
      return qrDataUrl;
    } catch (error) {
      console.error('生成二维码失败:', error);
      return '';
    }
  }, []);

  // 生成PDF
  const generatePDF = useCallback(async (
    options: PDFGenerationOptions = {}
  ): Promise<jsPDF> => {
    if (!certificateRef.current) {
      throw new Error('合格证元素未找到');
    }

    setIsGenerating(true);

    try {
      // 生成二维码
      await generateQRCode(finalQrCodeContent);

      // 等待一小段时间确保二维码渲染完成
      await new Promise(resolve => setTimeout(resolve, 300));

      console.log('开始生成PDF, 元素尺寸:', {
        width: certificateRef.current.offsetWidth,
        height: certificateRef.current.offsetHeight
      });

      // 使用html2canvas截取合格证元素 - 优化设置
      const canvas = await html2canvas(certificateRef.current, {
        scale: 3, // 降低到3倍缩放，平衡质量和性能
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: certificateRef.current.offsetWidth,
        height: certificateRef.current.offsetHeight,
        logging: true, // 开启日志查看错误
        imageTimeout: 15000
      });

      console.log('Canvas生成成功, 尺寸:', {
        width: canvas.width,
        height: canvas.height
      });

      // 检查canvas是否有效
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas尺寸为0，可能是元素渲染失败');
      }

      // 创建PDF
      const pdf = new jsPDF({
        orientation: options.orientation || 'portrait',
        unit: 'mm',
        format: options.format || 'a4'
      });

      // 计算图片在PDF中的尺寸
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      console.log('PDF页面尺寸:', {
        pageWidth: imgWidth,
        pageHeight: pdf.internal.pageSize.getHeight(),
        imgWidth,
        imgHeight
      });

      // 添加图片到PDF - 使用JPEG格式以确保兼容性
      const imageData = canvas.toDataURL('image/jpeg', 0.95);
      console.log('图片数据长度:', imageData.length);
      
      // 检查图片数据是否有效
      if (!imageData || imageData.length < 1000) {
        throw new Error('图片数据无效或太小');
      }
      
      pdf.addImage(
        imageData,
        'JPEG',
        0,
        0,
        imgWidth,
        imgHeight
      );

      console.log('PDF生成完成');
      return pdf;
    } catch (error) {
      console.error('PDF生成失败:', error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, [finalQrCodeContent, generateQRCode]);

  // 下载PDF
  const downloadPDF = useCallback(async (
    filename?: string,
    options?: PDFGenerationOptions
  ): Promise<void> => {
    try {
      const pdf = await generatePDF(options);
      const finalFilename = filename || `certificate-${certificateId || Date.now()}.pdf`;
      pdf.save(finalFilename);
    } catch (error) {
      console.error('PDF下载失败:', error);
      alert('PDF下载失败，请重试');
    }
  }, [generatePDF, certificateId]);

  // 初始化二维码
  useEffect(() => {
    generateQRCode(finalQrCodeContent);
  }, [finalQrCodeContent, generateQRCode]);

  return {
    generatePDF: () => generatePDF(),
    downloadPDF: (filename?: string) => downloadPDF(filename),
    isGenerating,
    qrCodeDataUrl,
    certificateRef,
    reportId,
    reportUrl
  };
};